import React, { useState, useEffect } from 'react';
import { Phone } from 'lucide-react';

const CallbacksTab = ({ BACKEND_URL }) => {
  const [callbackRequests, setCallbackRequests] = useState([]);

  const fetchCallbackRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/callback-requests`);
      if (!response.ok) throw new Error('Failed to fetch callback requests');
      const data = await response.json();
      setCallbackRequests(data);
    } catch (err) {
      console.error('Error fetching callback requests:', err.message);
      setCallbackRequests([]);
    }
  };

  useEffect(() => { fetchCallbackRequests(); }, []);

  const handleUpdateCallbackStatus = async (requestId, status) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/callback-requests/${requestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) fetchCallbackRequests();
    } catch (err) {
      console.error('Error updating callback request:', err.message);
    }
  };

  const pendingCount = callbackRequests.filter(r => r.status === 'pending').length;
  const completedCount = callbackRequests.filter(r => r.status === 'completed').length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 data-testid="callbacks-title" className="text-2xl font-bold text-[#1a4d2e] flex items-center gap-2">
              Callback Requests
            </h2>
            <p className="text-gray-500 mt-1">Farmers requesting a call back</p>
          </div>
          <div className="flex gap-2">
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
              {pendingCount} Pending
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              {completedCount} Completed
            </span>
          </div>
        </div>

        {callbackRequests.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">No callback requests yet</h3>
            <p className="text-gray-500 mt-2">When farmers request a callback, they'll appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {callbackRequests.map((request) => (
              <div
                key={request.id}
                data-testid={`callback-item-${request.id}`}
                className={`border rounded-lg p-4 ${
                  request.status === 'pending'
                    ? 'border-orange-300 bg-orange-50'
                    : request.status === 'completed'
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <a
                        href={`tel:${request.phone}`}
                        className="text-2xl font-bold text-[#1a4d2e] hover:text-[#ff6b00] flex items-center gap-2"
                      >
                        <Phone className="w-6 h-6" />
                        {request.phone}
                      </a>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        request.status === 'pending'
                          ? 'bg-orange-200 text-orange-800'
                          : request.status === 'completed'
                          ? 'bg-green-200 text-green-800'
                          : 'bg-gray-200 text-gray-800'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Name:</strong> {request.name}</p>
                      <p><strong>Preferred time:</strong> {request.preferred_time}</p>
                      <p><strong>Requested:</strong> {new Date(request.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex flex-row sm:flex-col gap-2">
                    <a
                      href={`tel:${request.phone}`}
                      data-testid={`callback-call-${request.id}`}
                      className="flex-1 sm:flex-none px-4 py-2 bg-[#ff6b00] text-white rounded-lg hover:bg-[#ff6b00]/90 font-medium text-center flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" />
                      Call Now
                    </a>
                    {request.status === 'pending' && (
                      <button
                        onClick={() => handleUpdateCallbackStatus(request.id, 'completed')}
                        data-testid={`callback-done-${request.id}`}
                        className="flex-1 sm:flex-none px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                      >
                        Mark Done
                      </button>
                    )}
                    {request.status === 'completed' && (
                      <button
                        onClick={() => handleUpdateCallbackStatus(request.id, 'pending')}
                        data-testid={`callback-reopen-${request.id}`}
                        className="flex-1 sm:flex-none px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium text-sm"
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallbacksTab;
